import axios from 'axios';
import { Question, QuizQuestion, Category, QuizSettings } from '../types/quiz';

const BASE_URL = 'https://opentdb.com';

// Shuffle array using Fisher-Yates algorithm
export const shuffleArray = (array: any[]): any[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Decode HTML entities in text
export const decodeHTML = (html: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

// Helper function to delay execution (for retry logic)
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Fetch quiz questions with retry logic
export const fetchQuizQuestions = async (
  settings: QuizSettings, 
  retryCount = 0, 
  maxRetries = 3, 
  batchSize?: number
): Promise<QuizQuestion[]> => {
  try {
    const { amount, category, difficulty, type, mode } = settings;
    
    // For endless mode, we'll fetch questions in smaller batches
    const questionAmount = batchSize || (mode === 'endless' ? 10 : amount);
    
    let url = `${BASE_URL}/api.php?amount=${questionAmount}`;
    
    if (category !== 'any') {
      url += `&category=${category}`;
    }
    
    if (difficulty !== 'any') {
      url += `&difficulty=${difficulty}`;
    }
    
    if (type !== 'any') {
      url += `&type=${type}`;
    }
    
    try {
      const response = await axios.get(url);
      
      if (response.data.response_code !== 0) {
        // Handle different response codes from the API
        switch (response.data.response_code) {
          case 1:
            throw new Error('Not enough questions available for these settings. Try reducing the number of questions or changing the category.');
          case 2:
            throw new Error('Invalid parameter in the API request. Please try different settings.');
          case 3:
            throw new Error('Session token not found. Please refresh the page and try again.');
          case 4:
            throw new Error('Session token has retrieved all available questions. Please try a different category.');
          default:
            throw new Error('Failed to fetch questions. Please try different settings.');
        }
      }
      
      // Process questions to decode HTML entities and add shuffled answers
      return response.data.results.map((question: Question) => {
        const decodedQuestion = {
          ...question,
          question: decodeHTML(question.question),
          correct_answer: decodeHTML(question.correct_answer),
          incorrect_answers: question.incorrect_answers.map(decodeHTML)
        };
        
        // Combine correct and incorrect answers and shuffle
        const all_answers = shuffleArray([
          decodedQuestion.correct_answer,
          ...decodedQuestion.incorrect_answers
        ]);
        
        return {
          ...decodedQuestion,
          all_answers
        };
      });
    } catch (axiosError: any) {
      // Handle rate limiting (429 error)
      if (axiosError.response && axiosError.response.status === 429) {
        if (retryCount < maxRetries) {
          // Exponential backoff: wait longer between each retry
          const waitTime = Math.pow(2, retryCount) * 1000;
          console.log(`Rate limited. Retrying in ${waitTime}ms... (Attempt ${retryCount + 1}/${maxRetries})`);
          await delay(waitTime);
          return fetchQuizQuestions(settings, retryCount + 1, maxRetries);
        } else {
          throw new Error('The quiz API is currently rate limited. Please wait a moment and try again.');
        }
      }
      throw axiosError;
    }
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error('An unknown error occurred');
  }
};

// Fetch categories
export const fetchCategories = async (): Promise<Category[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/api_category.php`);
    return response.data.trivia_categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};
